import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getEthToUsdRate } from '../services/walletServiceEnhanced';

const STATIC_COINS = [
    {
        id: 'bitcoin',
        symbol: 'BTC',
        name: 'Bitcoin',
        icon: 'bx-bitcoin',
        iconColor: '#f7931a',
        iconBg: 'rgba(247,147,26,0.15)',
        price: 67420.00,
        change24h: +2.34,
        marketCap: '1.33T',
        live: false,
    },
    {
        id: 'solana',
        symbol: 'SOL',
        name: 'Solana',
        icon: 'bx-coin-stack',
        iconColor: '#9945ff',
        iconBg: 'rgba(153,69,255,0.15)',
        price: 172.45,
        change24h: -1.12,
        marketCap: '79.8B',
        live: false,
    },
];

function formatPrice(n) {
    if (n >= 1000) return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

function MarketTrends() {
    const [ethPrice, setEthPrice] = useState(null);
    const [ethLoading, setEthLoading] = useState(true);

    useEffect(() => {
        getEthToUsdRate().then(result => {
            setEthPrice((!result.error && result.data?.rate) ? result.data.rate : null);
            setEthLoading(false);
        });
    }, []);

    const allCoins = [
        {
            id: 'ethereum',
            symbol: 'ETH',
            name: 'Ethereum',
            icon: 'bx-diamond',
            iconColor: '#627eea',
            iconBg: 'rgba(98,126,234,0.15)',
            price: ethPrice,
            change24h: null,
            marketCap: '387.2B',
            live: true,
            loading: ethLoading,
        },
        ...STATIC_COINS,
    ];

    return (
        <main className="main-sectionWrapper">
            <div id="main-section" className="primary-scroll">

                <div style={s.headerBar}>
                    <Link to="/welcome" style={s.backBtn} aria-label="Back">
                        <i className='bx bx-chevron-left' style={s.backIcon}></i>
                    </Link>
                    <span style={s.headerTitle}>Market Trends</span>
                    <div style={{ width: 38 }} />
                </div>

                <div style={s.container}>

                    <div style={s.notice}>
                        <i className="bx bx-info-circle" style={{ fontSize: 14, color: '#19bfb1', flexShrink: 0 }} />
                        <span style={s.noticeText}>ETH price is live. BTC and SOL are indicative only.</span>
                    </div>

                    <div style={s.coinList}>
                        {allCoins.map((coin, idx) => (
                            <div
                                key={coin.id}
                                style={{ ...s.coinRow, ...(idx < allCoins.length - 1 ? s.coinRowBorder : {}) }}
                            >
                                <div style={{ ...s.coinIcon, background: coin.iconBg }}>
                                    <i className={`bx ${coin.icon}`} style={{ color: coin.iconColor, fontSize: 22 }} />
                                </div>
                                <div style={s.coinInfo}>
                                    <div style={s.coinName}>{coin.name}</div>
                                    <div style={s.coinSymbol}>{coin.symbol}</div>
                                </div>
                                <div style={s.coinRight}>
                                    {coin.loading ? (
                                        <div style={s.priceSkeleton} />
                                    ) : (
                                        <div style={s.coinPrice}>
                                            {coin.price != null ? `$${formatPrice(coin.price)}` : '—'}
                                            {coin.live && (
                                                <span style={s.liveBadge}>
                                                    <span style={s.liveDot} />
                                                    Live
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {coin.change24h != null && (
                                        <div style={{ ...s.coinChange, color: coin.change24h >= 0 ? '#10b981' : '#ef4444' }}>
                                            {coin.change24h >= 0 ? '+' : ''}{coin.change24h.toFixed(2)}%
                                        </div>
                                    )}
                                    {coin.marketCap && (
                                        <div style={s.coinMcap}>MCap ${coin.marketCap}</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </div>

            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 0.4; }
                    50%       { opacity: 0.8; }
                }
                @keyframes livePulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%       { opacity: 0.4; transform: scale(0.7); }
                }
            `}</style>
        </main>
    );
}

const s = {
    headerBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 14px',
        background: 'rgba(14,7,88,0.88)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
    },
    backBtn: {
        width: 38,
        height: 38,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textDecoration: 'none',
        flexShrink: 0,
    },
    backIcon: { color: '#fff', fontSize: 22 },
    headerTitle: {
        color: '#fff',
        fontSize: 16,
        fontWeight: 700,
        letterSpacing: '-0.2px',
        flex: 1,
        textAlign: 'center',
    },
    container: {
        padding: '16px 16px 96px',
        maxWidth: 600,
        margin: '0 auto',
    },
    notice: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: 'rgba(25,191,177,0.08)',
        border: '1px solid rgba(25,191,177,0.2)',
        borderRadius: 12,
        padding: '10px 14px',
        marginBottom: 16,
    },
    noticeText: {
        color: 'rgba(255,255,255,0.55)',
        fontSize: 12,
    },
    coinList: {
        background: 'rgba(255,255,255,0.025)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 20,
        overflow: 'hidden',
    },
    coinRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '16px',
    },
    coinRowBorder: { borderBottom: '1px solid rgba(255,255,255,0.05)' },
    coinIcon: {
        width: 48,
        height: 48,
        borderRadius: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    coinInfo: { flex: 1 },
    coinName: {
        color: '#fff',
        fontSize: 15,
        fontWeight: 600,
        marginBottom: 2,
    },
    coinSymbol: {
        color: 'rgba(255,255,255,0.4)',
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    coinRight: { textAlign: 'right', flexShrink: 0 },
    coinPrice: {
        color: '#fff',
        fontSize: 16,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        justifyContent: 'flex-end',
        marginBottom: 3,
    },
    liveBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: 'rgba(25,191,177,0.12)',
        border: '1px solid rgba(25,191,177,0.25)',
        color: '#19bfb1',
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 7px',
        borderRadius: 20,
    },
    liveDot: {
        width: 5,
        height: 5,
        borderRadius: '50%',
        background: '#19bfb1',
        display: 'inline-block',
        animation: 'livePulse 2s ease infinite',
    },
    priceSkeleton: {
        width: 80,
        height: 18,
        borderRadius: 6,
        background: 'rgba(255,255,255,0.08)',
        animation: 'pulse 1.5s ease infinite',
        marginBottom: 6,
    },
    coinChange: {
        fontSize: 13,
        fontWeight: 600,
        marginBottom: 2,
    },
    coinMcap: {
        color: 'rgba(255,255,255,0.35)',
        fontSize: 11,
    },
};

export default MarketTrends;
